import { useTranslation } from 'react-i18next'

import { LearnArticleCta } from '@/features/aprender/LearnArticleCta'
import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { Formula, LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.que-es-el-food-cost.${field}`

export function QueEsElFoodCostArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'pregunta', label: t(k('toc.1'), '1. «¿Tú en qué food cost estás?»') },
        { id: 'formula', label: t(k('toc.2'), '2. La fórmula, esa sí es una sola') },
        { id: 'teorico-real', label: t(k('toc.3'), '3. El coste de la receta no es el de la caja') },
        { id: 'escala', label: t(k('toc.4'), '4. Un food cost por plato y otro de negocio') },
        { id: 'rango', label: t(k('toc.5'), '5. Un rango que no es universal') },
        { id: 'esta-semana', label: t(k('toc.6'), '6. Qué mirar esta semana') },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'El food cost es el porcentaje del precio de venta que se va en ingredientes: coste por ración dividido entre el precio sin IVA, multiplicado por cien. Esa parte es simple. Lo que cambia es qué coste metes en la cuenta.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(k('heroAlt'), 'Plato emplatado en el pase junto a una libreta de costes')}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                                {t(
                                    k('intro'),
                                    'Confundir el food cost teórico con el real, o el de un plato con el del negocio, es la forma más rápida de fijar un precio que parece correcto y no lo es.',
                                )}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='pregunta' className={articleUi.h2}>
                        {t(k('h2.pregunta'), '«¿Tú en qué food cost estás?»')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.pregunta.1'),
                                'Es la pregunta que se hace en cualquier feria o asociación de hostelería.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.pregunta.2'),
                                'Y todo el mundo suelta un número, como si fuera un dato único y fijo: «yo en el 28», «nosotros rondamos el 32».',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.pregunta.3'), 'Lo dicen como si el food cost fuera una sola cosa. No lo es.')}
                        </p>
                    </div>

                    <h2 id='formula' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.formula'), 'La fórmula, esa sí es una sola')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.formula.1'), 'Aquí no hay discusión. El food cost se calcula así:')}
                        </p>
                        <Formula variant='tight'>
                            {t(
                                k('formula.foodCost'),
                                'Food cost (%) = Coste de los ingredientes ÷ Precio de venta sin IVA × 100',
                            )}
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.formula.2Before'),
                                'Ejemplo, estimación propia: un plato de pasta que cuesta 3,20 € en ingredientes y se vende a 12,50 € con IVA, es decir, ',
                            )}
                            <span className={articleUi.em}>{t(k('p.formula.2Strong'), '11,36 € sin IVA')}</span>
                            {t(k('p.formula.2Mid'), ' (12,50 € ÷ 1,10). El food cost es 3,20 € ÷ 11,36 € × 100 = ')}
                            <span className={articleUi.em}>{t(k('p.formula.2Result'), '28,2 %')}</span>
                            {t(k('p.formula.2After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.formula.3'),
                                'Hasta aquí todo el mundo está de acuerdo. El problema empieza en qué «3,20 €» metes en la fórmula.',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.stats}>
                        <div className={articleUi.stat}>
                            <p className={articleUi.statLabel}>{t(k('stat.cost'), 'Coste ingredientes')}</p>
                            <p className={articleUi.statValueSm}>{t(k('stat.costValue'), '3,20 €')}</p>
                        </div>
                        <div className={articleUi.stat}>
                            <p className={articleUi.statLabel}>{t(k('stat.priceNoVat'), 'Precio sin IVA')}</p>
                            <p className={articleUi.statValueSm}>{t(k('stat.priceNoVatValue'), '11,36 €')}</p>
                        </div>
                        <div className={articleUi.statAccent}>
                            <p className={articleUi.statLabelAccent}>{t(k('stat.foodCost'), 'Food cost')}</p>
                            <p className={cn(articleUi.statValueSm, 'text-article-ember')}>
                                {t(k('stat.foodCostValue'), '28,2 %')}
                            </p>
                        </div>
                    </div>

                    <h2 id='teorico-real' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.teoricoReal'), 'El coste de la receta no es el coste de la caja')}
                    </h2>
                    <div className={cn(articleUi.prose, articleUi.pGapLg)}>
                        <p className={articleUi.p}>
                            {t(
                                k('p.teoricoReal.lead'),
                                'Hay dos food cost distintos, y casi nadie los distingue en voz alta:',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.compare}>
                        <div className={articleUi.compareCard}>
                            <p className={articleUi.compareLabel}>
                                {t(k('compare.theoretical.label'), 'Food cost teórico')}
                            </p>
                            <p className={articleUi.compareTitle}>
                                {t(k('compare.theoretical.title'), 'El número al que aspiras')}
                            </p>
                            <p className={articleUi.compareBody}>
                                {t(
                                    k('compare.theoretical.body'),
                                    'Sale del escandallo, con los pesos y precios de la receta tal y como está escrita.',
                                )}
                            </p>
                        </div>
                        <div className={articleUi.compareCard}>
                            <p className={articleUi.compareLabelAlert}>
                                {t(k('compare.actual.label'), 'Food cost real')}
                            </p>
                            <p className={articleUi.compareTitle}>
                                {t(k('compare.actual.title'), 'El número que paga las facturas')}
                            </p>
                            <p className={articleUi.compareBody}>
                                {t(
                                    k('compare.actual.body'),
                                    'Sale de lo que de verdad compraste y consumiste, calculado sobre existencias e inventario.',
                                )}
                            </p>
                        </div>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.teoricoReal.1Before'),
                                'Sigamos con la pasta. El escandallo decía 3,20 € por ración. Pero las compras reales de ese mes, divididas entre las raciones que de verdad saliste a vender, arrojan ',
                            )}
                            <span className={articleUi.em}>{t(k('p.teoricoReal.1Amount'), '3,65 €')}</span>
                            {t(k('p.teoricoReal.1After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <span className={articleUi.em}>
                                {t(k('p.teoricoReal.2Em'), 'Tu food cost real es del 32,1 %, no el 28,2 % que creías.')}
                            </span>{' '}
                            {t(
                                k('p.teoricoReal.2After'),
                                'Ahí dentro está la merma, las raciones mal servidas y la subida de precio del proveedor que nadie llevó a la ficha técnica.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.teoricoReal.3Before'), 'Esa brecha es la misma lógica que en ')}
                            <LearnArticleLink slug='coste-teorico-vs-coste-real-cocina'>
                                {t(k('linkTeoricoReal'), 'coste teórico vs coste real')}
                            </LearnArticleLink>
                            {t(k('p.teoricoReal.3After'), '.')}
                        </p>
                    </div>

                    <h2 id='escala' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.escala'), 'Un food cost por plato y otro para todo el negocio')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.escala.1'),
                                'El segundo lío es de escala. El 28,2 % de la pasta es el food cost de ese plato. El food cost de tu restaurante es otra cuenta: el coste de lo consumido en el mes (existencias iniciales + compras − existencias finales) dividido entre las ventas sin IVA.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.escala.2'),
                                'Puedes tener platos al 20 % conviviendo con platos al 45 % en la misma carta, y aun así cerrar el mes con un food cost global saludable, porque el volumen de ventas de cada uno pesa distinto. Mirar solo el porcentaje agregado esconde qué plato concreto te está comiendo el margen.',
                            )}
                        </p>
                    </div>

                    <h2 id='rango' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.rango'), 'Un rango que no es universal')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.rango.1'),
                                'Vas a leer en todas partes que el food cost sano está entre el 25 % y el 35 %. Es una referencia útil, no una ley.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.rango.2Before'),
                                'Esa cifra circula sobre todo en fuentes anglosajonas y no está medida sobre el mercado español. Una marisquería y una pizzería para llevar no funcionan con el mismo food cost, y no hay un estudio público que fije el rango correcto para cada tipo de negocio en España. Lo tratamos con más detalle en ',
                            )}
                            <LearnArticleLink slug='que-food-cost-deberia-tener-restaurante-espana'>
                                {t(k('linkRango'), 'qué food cost te conviene según tu carta')}
                            </LearnArticleLink>
                            {t(k('p.rango.2After'), '.')}
                        </p>
                    </div>

                    <section id='esta-semana' className={articleUi.dark}>
                        <h2 className={articleUi.h2}>{t(k('h2.estaSemana'), 'Qué mirar esta semana')}</h2>
                        <div className={articleUi.prose}>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('p.estaSemana.1Before'),
                                    'Coge un plato que vendas mucho y calcula su food cost teórico con el ',
                                )}
                                <LearnArticleLink slug='como-hacer-un-escandallo-paso-a-paso'>
                                    {t(k('linkEscandallo'), 'escandallo paso a paso')}
                                </LearnArticleLink>
                                {t(
                                    k('p.estaSemana.1After'),
                                    '. Después compáralo con lo que de verdad has comprado ese mes para hacerlo.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('p.estaSemana.2'),
                                    'Si la diferencia es de más de dos o tres puntos, ahí tienes una fuga que la receta nunca te va a contar.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('p.estaSemana.3Before'),
                                    'Y una vez tengas el food cost claro, el número que de verdad decide si el plato te conviene es el margen en euros, no el porcentaje: lo explicamos en ',
                                )}
                                <LearnArticleLink slug='food-cost-y-margen-bruto-restaurante'>
                                    {t(k('linkMargen'), 'food cost y margen bruto')}
                                </LearnArticleLink>
                                {t(k('p.estaSemana.3After'), '.')}
                            </p>
                            <p className={articleUi.pMuted}>
                                {t(k('p.estaSemana.tipiBefore'), 'Nosotros construimos ')}
                                <TipiLink />
                                {t(
                                    k('p.estaSemana.tipiAfter'),
                                    ' precisamente para esto: que sepas si estás hablando del food cost teórico o del real sin tener que llevar dos hojas de cálculo distintas.',
                                )}
                            </p>
                        </div>
                    </section>
                </ArticleReadingLayout>

                <LearnArticleCta />
                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        <li>
                            {t(
                                k('sources.1'),
                                'Definiciones y fórmula: elaboración propia de Tipi, a partir de la práctica estándar de escandallo en hostelería.',
                            )}
                        </li>
                        <li>
                            {t(
                                k('sources.2'),
                                'Ejemplo numérico: estimación propia, sin relación con datos de clientes reales.',
                            )}
                        </li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
